import React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import PerfectScrollbar from "react-perfect-scrollbar"
import { useTheme } from '@emotion/react'
import { TabContext, TabPanel } from '@mui/lab';
import { Box, Grid, IconButton, Tooltip, useMediaQuery } from '@mui/material'
import { makeStyles } from "@mui/styles"
import { OpenInNew, VpnKey } from '@mui/icons-material'
import Cookies from 'js-cookie'
import BreadcrumbHeader from '../../../layout/BreadcrumbsHeader'
import ThemeBGWrapper from '../../../ui-components/ThemeBGWrapper'
import CustomTabs from '../../../ui-components/CustomTabs'
import NeedHelpButton from 'ui-components/NeedHelpButton'
import { containerRef, navigateToDomain, singleSignOn } from '../../../utils'
import { useToast } from 'context/ToastContext'
import { GenerateUserTokenByAdminOnly } from 'api/backrr_startups'
import { FetchViewInvestorDetails } from 'api/backrr_investor'
import EmailLogs from '../../backrr_startups/viewDetails/EmailLogs'
import AdminNotes from '../../backrr_startups/viewDetails/AdminNotes'
import Portfolio from '../../portfolio/Portfolio'
import Reports from '../../reports/Reports'
import InvestorInformation, { investorDisplayName } from './InvestorInformation'

const useStyles = makeStyles(() => ({
    scrollTabs: {
        '& > .Mui-disabled.MuiTabs-scrollButtons': {
            display: 'none',
            transition: 'all 1s ease'
        }
    }
}));

const investorTabs = [
    { label: "Profile", value: "view-profile" },
    { label: "Portfolio", value: "portfolio" },
    { label: "Reports", value: "reports" },
    { label: "Email Logs", value: "email-logs" },
    { label: "Admin Notes", value: "admin-notes" },
]

const ViewInvestorDetails = () => {
    const { tab: tabValue } = useParams();
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))
    const navigate = useNavigate();
    const classes = useStyles();
    const { addToast } = useToast();
    const [searchParams] = useSearchParams();

    // The id stays in the URL so the page can be shared or reloaded; the cookie keeps the edit form working.
    const investor_id = searchParams.get("investor_id")?.trim() || Cookies.get("backrr_investor_id")?.trim();
    React.useEffect(() => {
        if (investor_id) Cookies.set("backrr_investor_id", investor_id);
        else navigate("/backrr_investors", { replace: true });
    }, [investor_id, navigate]);

    const value = investorTabs.some(item => item.value === tabValue) ? tabValue : "view-profile";
    const handleChange = (event, newValue) => {
        navigate(`/backrr_investors/${newValue}?investor_id=${investor_id}`, { replace: true });
    };

    const { data: investorDetails, isLoading } = FetchViewInvestorDetails(investor_id)
    const investor = investorDetails?.data;

    const { mutateAsync: provideSSOtoAdminAsync } = GenerateUserTokenByAdminOnly();

    const scrollStyle = { height: isLargeScreen ? "calc(100dvh - 190px)" : "calc(100dvh - 185px)", paddingInline: isSmallScreen ? "1rem" : "2rem" };

    return (
        <ThemeBGWrapper bgColor={theme.palette.customColors.yellow_bg}>
            <BreadcrumbHeader title={() => <Box display={"flex"} alignItems={"center"}>
                {isLoading ? "Loading..." : isSmallScreen ? "View Details" : investorDisplayName(investor) || "View Details"}
                {investor?.slug && <Tooltip title="View Profile">
                    <IconButton onClick={() => window.open(navigateToDomain("main", "investor/" + investor?.slug), '_blank')}>
                        <OpenInNew sx={{ height: 25 }} />
                    </IconButton>
                </Tooltip>}
                {investor?.email && <Tooltip title="Single Sign-On (SSO)">
                    <IconButton onClick={() => {
                        provideSSOtoAdminAsync({ payload: { email: investor?.email } })?.then((response) => {
                            if (response?.data?.statusCode === 200) {
                                window.open(singleSignOn(null, "investor") + "/cHJpdmF0ZS1yb3V0ZQ==?access-token=" + response?.data?.token, "_blank");
                            }
                            else {
                                addToast("User Not Found", "error")
                            }
                        })
                    }}>
                        <VpnKey fontSize='1.5rem' />
                    </IconButton>
                </Tooltip>}
            </Box>} Actions={() => <NeedHelpButton />} sx={{ paddingBottom: 0 }} />
            {isLoading ?
                <Grid item xs={12} height={"100dvh"} sx={{ width: '100%', padding: "2rem", paddingTop: "1rem" }}>
                    <Box className='skeleton' height={80}></Box>
                    <Box className='skeleton' height={"calc(100% - 80px)"} marginTop={2}></Box>
                </Grid>
                : <Grid xs={12} container height={"100%"}>
                    <Box sx={{ width: '100%', typography: 'body1' }}>
                        <TabContext value={value}>
                            <Box px={isSmallScreen ? "1rem" : "2rem"}>
                                <CustomTabs data={investorTabs} value={value} isSmallScreen={isSmallScreen} className={classes} titleStyle={{ fontSize: '1.10rem' }} handleChange={handleChange} sx={{ paddingTop: '1rem' }} bgColor={theme.palette.customColors.yellow_bg} />
                            </Box>
                            <TabPanel value="view-profile" sx={{ paddingInline: 0, height: '100%' }}>
                                <PerfectScrollbar containerRef={containerRef} style={scrollStyle}>
                                    <InvestorInformation data={investor} />
                                </PerfectScrollbar>
                            </TabPanel>
                            <TabPanel value="portfolio" sx={{ paddingInline: 0, height: '100%' }}>
                                <PerfectScrollbar containerRef={containerRef} style={scrollStyle}>
                                    <Portfolio investor_id={investor_id} />
                                </PerfectScrollbar>
                            </TabPanel>
                            <TabPanel value="reports" sx={{ paddingInline: 0, height: '100%' }}>
                                <PerfectScrollbar containerRef={containerRef} style={scrollStyle}>
                                    <Reports investor_id={investor_id} />
                                </PerfectScrollbar>
                            </TabPanel>
                            <TabPanel value="email-logs" sx={{ paddingInline: 0, height: '100%' }}>
                                <PerfectScrollbar containerRef={containerRef} style={scrollStyle}>
                                    <EmailLogs investor_id={investor_id} />
                                </PerfectScrollbar>
                            </TabPanel>
                            <TabPanel value="admin-notes" sx={{ paddingInline: 0, height: '100%' }}>
                                <PerfectScrollbar containerRef={containerRef} style={scrollStyle}>
                                    <AdminNotes investor_id={investor_id} data={{ startup_name: investorDisplayName(investor) }} />
                                </PerfectScrollbar>
                            </TabPanel>
                        </TabContext>
                    </Box>
                </Grid>
            }
        </ThemeBGWrapper>
    )
}

export default ViewInvestorDetails
